// validateTemplate.ts — live, client-side template validation for instant in-editor warnings. Mirrors the
// backend `Gameplay/Editor/TemplateValidator.cs` (same rules); the backend copy is authoritative and gates
// Publish, this copy gives immediate feedback while authoring. Keep the two in sync.
import { GridDirection } from "../Models/CellData";
import { BOX_COLORS, MarbleColor } from "../Models/LevelData";
import { BOLT_LENGTH, BOLT_MIN_LENGTH, CellModifierType, ELEVATOR_SIZE, FROZEN_BOX_HITS, LASER_GATE_MIN_LENGTH, METAL_BOX_HITS, SLIDING_DOOR_MIN_LENGTH, } from "../Models/CellModifier";
import { LevelDifficulty } from "../Models/Editor/EditorLevel";
import { BOARD_SIZE, MAX_PIPE_SPAWNS, MAX_SUPER_TUNNEL_SPAWNS, MAX_TOOL_BOX_SPAWNS, MAX_TUNNEL_SPAWNS, SPLIT_COLUMNS, holdsHiddenBox, isWall, modifierCovers } from "./templateOps";
export var ValidationSeverity;
(function (ValidationSeverity) {
    ValidationSeverity[ValidationSeverity["Error"] = 0] = "Error";
    ValidationSeverity[ValidationSeverity["Warning"] = 1] = "Warning";
})(ValidationSeverity || (ValidationSeverity = {}));
export const MAX_PALETTE = BOX_COLORS.length; // the palette cannot be asked for more than it holds
export const WEDGE_COUNT = 4; // LevelCompiler.WedgeCount
export const SLICES_PER_UNIT = 3; // LevelData.BallsPerBox / LevelCompiler.HolesPerSlice
export const HOLES_PER_SLICE = 3; // LevelCompiler.HolesPerSlice — balls ONE slice can take
// One authored rate, as the BACKEND will see it. Two conversions matter and both used to be missing:
//   * `Math.fround` — JS numbers are doubles, the backend stores 32-bit floats. Without it 1e39 read as a
//     finite in-range double here and as ±∞ (an error) there, so Publish looked open and then failed; and
//     1.00000001 warned here while binding to exactly 1f and passing there. A hand-written float.MaxValue
//     constant does not do this correctly either — the true double→float overflow threshold is the
//     midpoint to 2^128, not float.MaxValue, so a decimal literal misjudges a whole interval.
//   * strings — templates load off Drive as `JSON.parse(text) as EditorLevel` with no schema checking, and
//     the backend binds with AllowReadingFromString, so "0.5" is a perfectly good rate there and must not
//     be flagged here. Anything else (null, booleans, objects) cannot bind at all: NaN, reported below.
function asStoredRate(raw) {
    if (typeof raw === "number") {
        return Math.fround(raw);
    }
    return typeof raw === "string" && raw.trim() !== "" ? Math.fround(Number(raw)) : Number.NaN;
}
// Follows a propeller's ties from propeller to propeller: true if they come back round to one already passed,
// so no box ever sets the chain off (mirrors TemplateValidator.TiesIntoLoop).
function tiesIntoLoop(start, cellAt) {
    const passed = new Set();
    let cur = start;
    while (cur && cur.isPropeller && !cur.isCleared) {
        const key = `${cur.x},${cur.y}`;
        if (passed.has(key)) {
            return true;
        }
        passed.add(key);
        const s = step(cur.propellerDirection ?? GridDirection.Top);
        cur = cellAt.get(`${cur.x + s.x},${cur.y + s.y}`);
    }
    return false;
}
function step(dir) {
    switch (dir) {
        case GridDirection.Top:
            return { x: 0, y: -1 };
        case GridDirection.Bottom:
            return { x: 0, y: 1 };
        case GridDirection.Left:
            return { x: -1, y: 0 };
        default:
            return { x: 1, y: 0 };
    }
}
// The gate's factor at a cell — mirrors LevelCompiler.GateInfo.FactorAt: a unit sitting ABOVE the gate row
// and inside its columns has its balls multiplied.
function gateFactor(gate, col, row) {
    if (!gate || row >= gate.y || col < gate.x || col > gate.x + gate.gateLength - 1) {
        return 1;
    }
    return Math.max(1, gate.gateMultiplier);
}
// How many slices the compiler will deal for this board (mirrors TemplateValidator.TotalSlices): one
// SLICES_PER_UNIT block per colour unit, multiplied where the unit's balls fall through the gate. A
// tunnel's spawns count at its OUTPUT cell — that is where their boxes appear, so that is the column the
// gate is judged on.
function totalSlices(cells, modifiers) {
    const gate = cells.find((c) => c.isGate && c.gateLength > 0);
    let total = 0;
    for (const c of cells) {
        if (c.isGate || c.isCleared || (c.isPropeller && !holdsHiddenBox(c))) {
            continue; // gate cells are walls, cleared cells bare ground, a bare propeller carries nothing — no balls, no slices
        }
        let x = c.x;
        let y = c.y;
        let units = 1;
        if (c.isTunnel) {
            const s = step(c.tunnelDirection);
            x += s.x;
            y += s.y;
            units = Math.max(0, c.tunnelSpawnCount);
        }
        else if (c.isToolBox) {
            // Targets are runtime-chosen, so the crate's own cell stands in for them (as the compiler does).
            units = Math.max(0, c.toolBoxSpawnCount ?? 0);
        }
        else if (c.isSuperTunnel) {
            // The neighbour it serves is chosen at runtime, so its own cell prices its boxes (as the compiler does).
            units = Math.max(0, c.superTunnelSpawnCount ?? 0);
        }
        else if (c.isPipe) {
            // A tunnel facing down: the cell below prices its boxes (as the compiler does).
            y += 1;
            units = Math.max(0, c.pipeSpawnCount ?? 0);
        }
        else if (c.isPropeller) {
            units = 1; // the plain box under it (a stale double flag on the cell is not its own)
        }
        else if (c.isDouble) {
            units = 2; // the double box and the box inside it, a whole box each (as the compiler deals them)
        }
        total += units * SLICES_PER_UNIT * gateFactor(gate, x, y);
    }
    for (const m of elevators(modifiers)) {
        const topLeft = elevatorTopLeft(m);
        for (let k = 0; k < ELEVATOR_BOXES; k++) {
            total += SLICES_PER_UNIT * gateFactor(gate, topLeft.x + (k % ELEVATOR_SIZE), topLeft.y + Math.floor(k / ELEVATOR_SIZE));
        }
    }
    return total;
}
const ELEVATOR_BOXES = ELEVATOR_SIZE * ELEVATOR_SIZE;
function elevators(modifiers) {
    return modifiers.filter((m) => m.type === CellModifierType.Elevator);
}
function elevatorTopLeft(m) {
    const mcells = Array.isArray(m.cells) ? m.cells : [];
    return mcells.length === 0
        ? { x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER }
        : { x: Math.min(...mcells.map((p) => p.x)), y: Math.min(...mcells.map((p) => p.y)) };
}
// A colour the game actually has: MarbleColor runs None(0)..White(13) with no gaps. Only a hand-edited file
// can carry anything else — the editor paints from the palette — but such a number would ride through the
// compiler into the level and leave the game looking for a sprite that does not exist.
function isKnownColor(color) {
    return Number.isInteger(color) && color >= MarbleColor.None && color <= MarbleColor.White;
}
export function hasErrors(issues) {
    return issues.some((i) => i.severity === ValidationSeverity.Error);
}
export function validateTemplate(t) {
    const issues = [];
    const err = (message, x = -1, y = -1) => issues.push({ severity: ValidationSeverity.Error, message, x, y });
    const warn = (message, x = -1, y = -1) => issues.push({ severity: ValidationSeverity.Warning, message, x, y });
    // Read the board bounds the way the backend binds them: a missing or unbindable key arrives as 0 there.
    // Left as `undefined`, every comparison below silently answers `false` — a template with no `columns`
    // key looked spotless in the editor while the backend answered with one error per cell, and since Save
    // now enforces that too, it turned into a Save that just fails.
    const columns = Number(t.columns) || 0;
    const rows = Number(t.rows) || 0;
    if (columns < 1 || rows < 1) {
        err(`Board size must be at least 1×1 (is ${columns}×${rows}).`);
    }
    else if (columns > BOARD_SIZE || rows > BOARD_SIZE) {
        // The game's board is a fixed BOARD_SIZE×BOARD_SIZE and this editor only ever builds that, but the
        // bounds come off the FILE and drive GridCanvas's empty-slot loop — rows×cols, however few cells the
        // board actually holds. Without this a file claiming 100000×100000 took the tab down on the first
        // draw, which is to say before anything could report it. (GridCanvas bounds what it draws for the
        // same reason; this is what tells the author why, and holds Save shut until it is fixed.)
        err(`Board size must be at most ${BOARD_SIZE}×${BOARD_SIZE} (is ${columns}×${rows}).`);
    }
    // `generation` is read straight off Drive too, so it can be absent entirely — and reading through it
    // threw inside render(), which has no error boundary above it: a bad file blanked the editor instead of
    // being reported. The backend says so and carries on (`Missing generation parameters.`); say it in the
    // same words, and read paletteSize the way the backend binds it — a missing key arrives there as 0.
    const gen = t.generation;
    const paletteSize = gen ? Number(gen.paletteSize) || 0 : 0;
    if (!gen) {
        err("Missing generation parameters.");
    }
    else if (paletteSize < 1 || paletteSize > MAX_PALETTE) {
        err(`Palette size must be 1..${MAX_PALETTE} (is ${paletteSize}).`);
    }
    // A probability outside [0,1] is an authoring slip, not a broken level — the compiler clamps it.
    // Name the wedge so the author knows which box to fix.
    // Array.isArray, not a truthiness test: this key is ours and it is read straight off Drive, so a
    // hand-edited `{}` or `"0.5"` in its place would take `chances.forEach` down — and a throw in render
    // unmounts the editor (there is no error boundary), turning a bad file into a blank page.
    const chances = gen && Array.isArray(gen.mysterySliceChances) ? gen.mysterySliceChances : null;
    if (chances) {
        chances.forEach((raw, i) => {
            const c = asStoredRate(raw);
            if (!Number.isFinite(c)) {
                // Name the value the way the backend would: ±∞ for something that is already infinite,
                // the authored number for a double that only becomes infinite once stored as a float.
                const shown = Number.isNaN(c) ? "NaN" : Number.isFinite(Number(raw)) ? `${raw}` : c > 0 ? "∞" : "-∞";
                err(`Mystery slice chance for wedge ${i} is ${shown}; it cannot be saved.`);
            }
            else if (c < 0 || c > 1) {
                // Say what is STORED and what the game will actually use — the two differ, and
                // "it will be treated as 0..1" hid that the out-of-range value stays in the file.
                warn(`Mystery slice chance for wedge ${i} must be 0..1 (is ${c}); the file keeps that value and the level compiles with ${Math.min(1, Math.max(0, c))}.`);
            }
        });
        if (chances.length > WEDGE_COUNT) {
            warn(`There are more mystery slice chances than wedges (${chances.length} for ${WEDGE_COUNT}); the extras are ignored.`);
        }
    }
    // `LevelDifficulty[x]` on a numeric TS enum also answers the NAME→value direction, so a stray string
    // "Hard" in a file used to read as a perfectly good difficulty here while the backend could not bind it
    // at all. Only a number is a difficulty.
    if (t.difficulty !== undefined && (typeof t.difficulty !== "number" || LevelDifficulty[t.difficulty] === undefined)) {
        warn(`Unknown difficulty value ${t.difficulty}; it will read as Normal.`);
    }
    // `cells`, and the two arrays under it, arrive unchecked off Drive — and a null ENTRY inside either one
    // binds perfectly well on the backend, where EditorCell and CellModifier are classes and both loops skip
    // nulls (`c == null`, `m?.Cells == null`). So a file the backend validates, saves and publishes without
    // complaint used to take the editor down with a TypeError in render(). Same shape as the C# copy: drop
    // what cannot be read, judge the rest.
    const cells = (Array.isArray(t.cells?.items) ? t.cells.items : []).filter((c) => c != null);
    const modifiers = (Array.isArray(t.cells?.modifiers) ? t.cells.modifiers : []).filter((m) => m != null);
    const seen = new Set();
    // What is AT each coordinate, for the rules that care about the kind of cell a modifier points at (the
    // C# copy reads its own `seen` the same way). First writer wins, matching which of two cells sharing a
    // coordinate `seen` reports as the original.
    const cellAt = new Map();
    for (const c of cells) {
        if (c.x < 0 || c.y < 0 || c.x >= columns || c.y >= rows) {
            err(`Cell (${c.x}, ${c.y}) is outside the ${columns}×${rows} board.`, c.x, c.y);
        }
        const key = `${c.x},${c.y}`;
        if (seen.has(key)) {
            err(`Two cells share coordinate (${c.x}, ${c.y}).`, c.x, c.y);
        }
        else {
            cellAt.set(key, c);
        }
        seen.add(key);
    }
    const colorUnits = cells.filter((c) => !isWall(c) || holdsHiddenBox(c)).length +
        cells.filter((c) => c.isTunnel).reduce((n, c) => n + Math.max(0, c.tunnelSpawnCount), 0) +
        cells.filter((c) => c.isToolBox).reduce((n, c) => n + Math.max(0, c.toolBoxSpawnCount ?? 0), 0) +
        cells.filter((c) => c.isSuperTunnel).reduce((n, c) => n + Math.max(0, c.superTunnelSpawnCount ?? 0), 0) +
        cells.filter((c) => c.isPipe).reduce((n, c) => n + Math.max(0, c.pipeSpawnCount ?? 0), 0) +
        elevators(modifiers).length * ELEVATOR_BOXES;
    if (colorUnits === 0) {
        err("The board has no boxes to play — add at least one box.");
    }
    // A wedge only hides slices behind its first one, so a pile shallower than two slices can never show
    // the mechanic. Slices are dealt round-robin, so of T slices wedge i gets ceil((T - i) / WEDGE_COUNT) —
    // the wedges are NOT equally deep. Each wedge is judged on its own depth, and every offending wedge is
    // named (mirrors TemplateValidator.cs).
    if (chances) {
        const total = totalSlices(cells, modifiers);
        chances.forEach((raw, i) => {
            // Judged on the STORED rate for the same reason the range check is: 1e-46 is a positive double
            // and a plain 0 once it is a float, so reading the double warned about a wedge the backend
            // considers switched off.
            if (i >= WEDGE_COUNT || !(asStoredRate(raw) > 0)) {
                return; // rate 0 (and NaN) hides nothing anyway, so depth cannot make it worse
            }
            const depth = i < total ? Math.floor((total - i - 1) / WEDGE_COUNT) + 1 : 0;
            if (depth < 2) {
                warn(`Wedge ${i} has a mystery slice chance but its pile is only ${depth} slice(s) deep — too small to show one, because the first slice of a pile is never hidden.`);
            }
        });
    }
    // Hand-painted colours (mirrors TemplateValidator.cs). The compiler ignores an override it cannot place;
    // these rules are what stop it doing so silently. A colour outside the generated palette is deliberately
    // NOT one of them — painting past the palette is how a designer reaches a colour the seed may not deal.
    for (const c of cells) {
        const painted = c.colorOverride ?? MarbleColor.None;
        if (painted === MarbleColor.None) {
            continue;
        }
        if (!isKnownColor(painted)) {
            err(`The box at (${c.x}, ${c.y}) is painted colour ${painted}, which the game does not have.`, c.x, c.y);
        }
        else if (c.isCleared) {
            warn(`The painted colour on the cleared cell at (${c.x}, ${c.y}) is ignored — a cleared cell holds no box.`, c.x, c.y);
        }
        else if (c.isGate) {
            warn(`The painted colour on the gate at (${c.x}, ${c.y}) is ignored — a gate is a wall and drops no balls.`, c.x, c.y);
        }
        else if (c.isTunnel) {
            warn(`The painted colour on the tunnel at (${c.x}, ${c.y}) is ignored — a tunnel's boxes are its spawns, and each spawn carries its own colour.`, c.x, c.y);
        }
        else if (c.isToolBox) {
            warn(`The painted colour on the tool box at (${c.x}, ${c.y}) is ignored — a tool box's boxes are its spawns, and each spawn carries its own colour.`, c.x, c.y);
        }
        else if (c.isSuperTunnel) {
            warn(`The painted colour on the super tunnel at (${c.x}, ${c.y}) is ignored — a super tunnel's boxes are its spawns, and each spawn carries its own colour.`, c.x, c.y);
        }
        else if (c.isPipe) {
            warn(`The painted colour on the pipe at (${c.x}, ${c.y}) is ignored — a pipe's boxes are its spawns, and each spawn carries its own colour.`, c.x, c.y);
        }
        else if (c.isPropeller && !holdsHiddenBox(c)) {
            warn(`The painted colour on the propeller at (${c.x}, ${c.y}) is ignored — a propeller holds no box; the box it hits supplies the screws.`, c.x, c.y);
        }
        else if (c.isSplit && !c.isDouble) {
            warn(`The painted colour on the split box at (${c.x}, ${c.y}) is ignored — a split box is painted column by column.`, c.x, c.y);
        }
    }
    // The colour painted on the box inside a double box (mirrors TemplateValidator.cs).
    for (const c of cells) {
        const inside = c.innerColorOverride ?? MarbleColor.None;
        if (inside === MarbleColor.None) {
            continue;
        }
        if (!isKnownColor(inside)) {
            err(`The box inside the double box at (${c.x}, ${c.y}) is painted colour ${inside}, which the game does not have.`, c.x, c.y);
        }
        else if (!c.isDouble || isWall(c)) {
            warn(`The inside colour painted on (${c.x}, ${c.y}) is ignored — it is not a double box.`, c.x, c.y);
        }
        else if (inside === (c.colorOverride ?? MarbleColor.None)) {
            warn(`The double box at (${c.x}, ${c.y}) and the box inside it are painted the same colour — the two will look like one.`, c.x, c.y);
        }
    }
    // A split box's painted columns (mirrors TemplateValidator.cs). Array.isArray for the reason every key
    // read straight off a file gets it: a hand-edited non-array must be reported, not crash render.
    for (const c of cells) {
        if (!Array.isArray(c.splitColorOverrides)) {
            continue;
        }
        const painted = c.splitColorOverrides;
        painted.forEach((p, k) => {
            if ((p ?? MarbleColor.None) !== MarbleColor.None && !isKnownColor(p)) {
                err(`Column ${k} of the split box at (${c.x}, ${c.y}) is painted colour ${p}, which the game does not have.`, c.x, c.y);
            }
        });
        const any = painted.some((p) => (p ?? MarbleColor.None) !== MarbleColor.None);
        if (any && (!c.isSplit || isWall(c) || c.isCleared)) {
            warn(`The column colours painted on (${c.x}, ${c.y}) are ignored — it is not a split box.`, c.x, c.y);
        }
        else if (painted.slice(SPLIT_COLUMNS).some((p) => (p ?? MarbleColor.None) !== MarbleColor.None)) {
            warn(`The split box at (${c.x}, ${c.y}) has ${painted.length} painted columns — only the first ${SPLIT_COLUMNS} are used.`, c.x, c.y);
        }
    }
    for (const c of cells) {
        (c.tunnelSpawns ?? []).forEach((spawn, k) => {
            const painted = spawn?.colorOverride ?? MarbleColor.None;
            if (painted !== MarbleColor.None && !isKnownColor(painted)) {
                err(`Spawn ${k} of the tunnel at (${c.x}, ${c.y}) is painted colour ${painted}, which the game does not have.`, c.x, c.y);
            }
        });
    }
    for (const c of cells) {
        (Array.isArray(c.toolBoxSpawns) ? c.toolBoxSpawns : []).forEach((spawn, k) => {
            const painted = spawn?.colorOverride ?? MarbleColor.None;
            if (painted !== MarbleColor.None && !isKnownColor(painted)) {
                err(`Spawn ${k} of the tool box at (${c.x}, ${c.y}) is painted colour ${painted}, which the game does not have.`, c.x, c.y);
            }
        });
    }
    for (const c of cells) {
        (Array.isArray(c.superTunnelSpawns) ? c.superTunnelSpawns : []).forEach((spawn, k) => {
            const painted = spawn?.colorOverride ?? MarbleColor.None;
            if (painted !== MarbleColor.None && !isKnownColor(painted)) {
                err(`Spawn ${k} of the super tunnel at (${c.x}, ${c.y}) is painted colour ${painted}, which the game does not have.`, c.x, c.y);
            }
        });
    }
    for (const c of cells) {
        (Array.isArray(c.pipeSpawns) ? c.pipeSpawns : []).forEach((spawn, k) => {
            const painted = spawn?.colorOverride ?? MarbleColor.None;
            if (painted !== MarbleColor.None && !isKnownColor(painted)) {
                err(`Spawn ${k} of the pipe at (${c.x}, ${c.y}) is painted colour ${painted}, which the game does not have.`, c.x, c.y);
            }
        });
    }
    for (const m of elevators(modifiers)) {
        const at = Array.isArray(m.cells) ? m.cells[0] : undefined;
        if (!at) {
            continue;
        }
        (Array.isArray(m.spawns) ? m.spawns : []).forEach((spawn, k) => {
            const painted = spawn?.colorOverride ?? MarbleColor.None;
            if (painted !== MarbleColor.None && !isKnownColor(painted)) {
                err(`Spawn ${k} of the elevator at (${at.x}, ${at.y}) is painted colour ${painted}, which the game does not have.`, at.x, at.y);
            }
        });
    }
    // A painted slice is addressed by WHERE it sits, so the board can move out from under it: shrink the
    // level and the slice it named is gone. The compiler skips such an entry rather than repainting the
    // nearest slice — the designer would not recognise the result — so this warning is the only trace.
    if (t.sliceColorOverrides) {
        const total = totalSlices(cells, modifiers);
        for (const paint of t.sliceColorOverrides) {
            if (!paint) {
                continue; // normalizeTemplate drops these at the door; nothing to judge
            }
            if (paint.color !== MarbleColor.None && !isKnownColor(paint.color)) {
                err(`The painted slice at wedge ${paint.wedge}, depth ${paint.depth} is colour ${paint.color}, which the game does not have.`);
                continue;
            }
            const inRange = paint.wedge >= 0 && paint.wedge < WEDGE_COUNT;
            const depth = inRange && paint.wedge < total ? Math.floor((total - paint.wedge - 1) / WEDGE_COUNT) + 1 : 0;
            if (!inRange || paint.depth < 0 || paint.depth >= depth) {
                warn(`The painted slice at wedge ${paint.wedge}, depth ${paint.depth} addresses no slice on this board (that pile is ${depth} deep) — the colour is ignored.`);
            }
        }
    }
    // A frozen slice is addressed the same way, so it goes stale the same way. Mirrors TemplateValidator.cs.
    if (t.frozenSlices) {
        const total = totalSlices(cells, modifiers);
        for (const frozen of t.frozenSlices) {
            if (!frozen) {
                continue;
            }
            const inRange = frozen.wedge >= 0 && frozen.wedge < WEDGE_COUNT;
            const depth = inRange && frozen.wedge < total ? Math.floor((total - frozen.wedge - 1) / WEDGE_COUNT) + 1 : 0;
            if (!inRange || frozen.depth < 0 || frozen.depth >= depth) {
                warn(`The frozen slice at wedge ${frozen.wedge}, depth ${frozen.depth} addresses no slice on this board (that pile is ${depth} deep) — it is not frozen.`);
            }
        }
    }
    const gateHeads = cells.filter((c) => c.isGate && c.gateLength > 0);
    // Several gates are fine — each one is priced (the backend rule since fix/multi-gate-slice-count).
    // What does NOT do what it looks like is STACKING them: the game multiplies a ball once, so a box
    // above two gates is multiplied by the nearer one only, and an author who placed x2 over x2
    // expecting x4 gets x2. Mirrors TemplateValidator.cs.
    for (const upper of gateHeads) {
        const lower = gateHeads.find((g) => g.y > upper.y && g.x <= upper.x + upper.gateLength - 1 && upper.x <= g.x + g.gateLength - 1);
        if (lower) {
            warn(`The gates at (${upper.x}, ${upper.y}) and (${lower.x}, ${lower.y}) are stacked in the same column — ` +
                "a ball is multiplied once, by the first gate it meets, so they do not combine.", upper.x, upper.y);
        }
    }
    for (const g of gateHeads) {
        if (g.gateMultiplier < 1) {
            warn(`Gate at (${g.x}, ${g.y}) has multiplier ${g.gateMultiplier}; treated as 1.`, g.x, g.y);
        }
        if (g.x + g.gateLength - 1 >= columns) {
            err(`Gate at (${g.x}, ${g.y}) spans ${g.gateLength} cells past the right edge.`, g.x, g.y);
            continue;
        }
        for (let dx = 1; dx < g.gateLength; dx++) {
            const cont = cells.find((c) => c.x === g.x + dx && c.y === g.y);
            if (!cont || !cont.isGate) {
                warn(`Gate at (${g.x}, ${g.y}) is missing its continuation cell at (${g.x + dx}, ${g.y}).`, g.x + dx, g.y);
            }
        }
    }
    for (const c of cells.filter((c) => c.isTunnel)) {
        if (c.tunnelSpawnCount <= 0) {
            warn(`Tunnel at (${c.x}, ${c.y}) has spawn count 0 — it spawns nothing.`, c.x, c.y);
        }
        // An Error, not a warning: the count drives one per-spawn record and one inspector row each, so a
        // file carrying a big one freezes the editor on the render that would have shown this. The ops clamp
        // what the editor itself authors (MAX_TUNNEL_SPAWNS), which leaves this to explain a FILE — and to
        // hold Save and Publish closed until the number is brought back into range.
        if (c.tunnelSpawnCount > MAX_TUNNEL_SPAWNS) {
            err(`Tunnel at (${c.x}, ${c.y}) spawns ${c.tunnelSpawnCount} boxes; the most a tunnel can spawn is ${MAX_TUNNEL_SPAWNS}.`, c.x, c.y);
        }
        const s = step(c.tunnelDirection);
        const ox = c.x + s.x;
        const oy = c.y + s.y;
        if (c.tunnelSpawnCount > 0 && (ox < 0 || oy < 0 || ox >= columns || oy >= rows)) {
            warn(`Tunnel at (${c.x}, ${c.y}) points off the board — its spawn cell (${ox}, ${oy}) is out of bounds.`, c.x, c.y);
        }
    }
    // Tool boxes (mirrors the C# copy). No direction to check, but the load has to be sane and it has to be
    // able to LEAVE: the crate throws all or nothing, so a board that can never free that many cells at once
    // leaves it shut for the whole level.
    const hasGate = cells.some((c) => c.isGate);
    for (const c of cells.filter((c) => c.isToolBox)) {
        const count = c.toolBoxSpawnCount ?? 0;
        if (c.isTunnel || c.isGate) {
            err(`Cell (${c.x}, ${c.y}) is a tool box AND a tunnel or gate — a cell can only be one of them.`, c.x, c.y);
        }
        if (count <= 0) {
            warn(`Tool box at (${c.x}, ${c.y}) has spawn count 0 — it throws nothing.`, c.x, c.y);
        }
        if (count > MAX_TOOL_BOX_SPAWNS) {
            err(`Tool box at (${c.x}, ${c.y}) throws ${count} boxes; the most a tool box can throw is ${MAX_TOOL_BOX_SPAWNS}.`, c.x, c.y);
        }
        const emptiable = cells.filter((o) => !isWall(o)).length;
        if (count > emptiable) {
            err(`Tool box at (${c.x}, ${c.y}) throws ${count} boxes but the board only ever has ${emptiable} cells that can be freed — it can never open.`, c.x, c.y);
        }
        if (hasGate && count > 0) {
            warn(`Tool box at (${c.x}, ${c.y}) shares the board with a gate — its boxes are priced at the crate's own side of the gate, so a throw that crosses it makes the ball total wrong.`, c.x, c.y);
        }
    }
    // Super tunnels. Like a tunnel it needs a sane load; unlike one it is triggered by a DROP beside it, never
    // by an empty cell — so one with no box next to it never spawns anything at all. (Mirrors the C# copy.)
    for (const c of cells.filter((c) => c.isSuperTunnel)) {
        const count = c.superTunnelSpawnCount ?? 0;
        if (c.isTunnel || c.isGate || c.isToolBox) {
            err(`Cell (${c.x}, ${c.y}) is a super tunnel AND a tunnel, gate or tool box — a cell can only be one of them.`, c.x, c.y);
        }
        if (count <= 0) {
            warn(`Super tunnel at (${c.x}, ${c.y}) has spawn count 0 — it spawns nothing.`, c.x, c.y);
        }
        if (count > MAX_SUPER_TUNNEL_SPAWNS) {
            err(`Super tunnel at (${c.x}, ${c.y}) holds ${count} boxes; the most a super tunnel can hold is ${MAX_SUPER_TUNNEL_SPAWNS}.`, c.x, c.y);
        }
        const boxBeside = [
            [c.x, c.y - 1],
            [c.x, c.y + 1],
            [c.x - 1, c.y],
            [c.x + 1, c.y],
        ].some(([nx, ny]) => {
            const o = cellAt.get(`${nx},${ny}`);
            return o !== undefined && !isWall(o);
        });
        if (count > 0 && !boxBeside) {
            warn(`Super tunnel at (${c.x}, ${c.y}) has no box beside it — it only refills a neighbour whose box is dropped, so it never spawns.`, c.x, c.y);
        }
    }
    // Pipes (mirrors TemplateValidator.cs). A tunnel that always faces DOWN, its body running up the column
    // above it into the board's top wall: it needs a sane load, a cell below to feed, and a clear column above
    // (an authored cell stops the body there instead of at the wall).
    for (const c of cells.filter((c) => c.isPipe)) {
        const count = c.pipeSpawnCount ?? 0;
        if (c.isTunnel || c.isGate || c.isToolBox || c.isSuperTunnel) {
            err(`Cell (${c.x}, ${c.y}) is a pipe AND a tunnel, gate, tool box or super tunnel — a cell can only be one of them.`, c.x, c.y);
        }
        if (count <= 0) {
            warn(`Pipe at (${c.x}, ${c.y}) has spawn count 0 — it spawns nothing.`, c.x, c.y);
        }
        if (count > MAX_PIPE_SPAWNS) {
            err(`Pipe at (${c.x}, ${c.y}) holds ${count} boxes; the most a pipe can hold is ${MAX_PIPE_SPAWNS}.`, c.x, c.y);
        }
        if (count > 0 && c.y + 1 >= rows) {
            warn(`Pipe at (${c.x}, ${c.y}) is on the bottom row — the cell it feeds, (${c.x}, ${c.y + 1}), is off the board.`, c.x, c.y);
        }
        for (let y = c.y - 1; y >= 0; y--) {
            if (cellAt.has(`${c.x},${y}`)) {
                warn(`Pipe at (${c.x}, ${c.y}) has the cell (${c.x}, ${y}) above it — its body runs up the column to the top wall and stops there instead. Keep the column above a pipe empty.`, c.x, c.y);
                break;
            }
        }
    }
    // Propellers (mirrors TemplateValidator.cs). Tied to the box beside it in its direction: dropping that box
    // launches it. It can also be tied to another propeller, which sets it off as it takes off itself. So the
    // tied cell has to be a box or a propeller, and a chain must not loop back on itself — anything else and it
    // never takes off, a wall for the whole level. It holds nothing, so a second kind on it is an Error and a mystery/arrow flag a Warning (glide,
    // split and double on a non-box are reported below, for every wall alike).
    for (const c of cells.filter((c) => c.isPropeller && !c.isCleared)) {
        if (c.isTunnel || c.isGate || c.isToolBox || c.isSuperTunnel || c.isPipe) {
            err(`Cell (${c.x}, ${c.y}) is a propeller AND a tunnel, gate, tool box, super tunnel or pipe — a cell can only be one of them.`, c.x, c.y);
            continue;
        }
        if (c.isMystery || c.isArrow) {
            warn(`The mystery/arrow flag on the propeller at (${c.x}, ${c.y}) is ignored — a propeller holds no box.`, c.x, c.y);
        }
        const s = step(c.propellerDirection ?? GridDirection.Top);
        const tx = c.x + s.x;
        const ty = c.y + s.y;
        const tied = cellAt.get(`${tx},${ty}`);
        if (!tied) {
            err(`Propeller at (${c.x}, ${c.y}) is tied to (${tx}, ${ty}), which holds no box — it can never take off.`, c.x, c.y);
        }
        else if (tied.isPropeller && !tied.isCleared) {
            if (tiesIntoLoop(c, cellAt)) {
                err(`Propeller at (${c.x}, ${c.y}) is tied into a loop of propellers with no box at the end — nothing ever sets it off, so it can never take off.`, c.x, c.y);
            }
        }
        else if (isWall(tied)) {
            err(`Propeller at (${c.x}, ${c.y}) is tied to (${tx}, ${ty}), which is not a box or a propeller — only a dropped box or a launching propeller sets it off, so it can never take off.`, c.x, c.y);
        }
    }
    // Cleared cells (mirrors TemplateValidator.cs). The compiler emits one as bare ground whatever else it
    // carries: a second KIND on it silently loses that item (an Error — balls and slices move with it), a
    // leftover box flag only loses something nothing could show (a Warning).
    for (const c of cells.filter((c) => c.isCleared)) {
        if (c.isGate || c.isTunnel || c.isToolBox || c.isSuperTunnel || c.isPipe || c.isPropeller) {
            err(`Cell (${c.x}, ${c.y}) is cleared AND a gate, tunnel, tool box, super tunnel, pipe or propeller — a cell can only be one of them.`, c.x, c.y);
        }
        else if (c.isMystery || c.isArrow || c.isGlide || c.isSplit || c.isDouble) {
            warn(`The mystery/arrow/glide/split/double flag on the cleared cell at (${c.x}, ${c.y}) is ignored — a cleared cell holds no box.`, c.x, c.y);
        }
    }
    // Glide on something that is not a box (mirrors TemplateValidator.cs): the compiler drops it — only a
    // box has screws to glide. Cleared cells are reported above.
    for (const c of cells.filter((c) => c.isGlide && !c.isCleared && isWall(c))) {
        warn(`The glide flag on (${c.x}, ${c.y}) is ignored — only a box can glide.`, c.x, c.y);
    }
    // Split on something that is not a box (mirrors TemplateValidator.cs). Cleared cells are reported above.
    for (const c of cells.filter((c) => c.isSplit && !c.isCleared && isWall(c))) {
        warn(`The split flag on (${c.x}, ${c.y}) is ignored — only a box can be split.`, c.x, c.y);
    }
    // A split box shows three colours; with a smaller palette the compiler cannot find three for it.
    // Double on something that is not a box (mirrors TemplateValidator.cs). Cleared cells are reported above.
    for (const c of cells.filter((c) => c.isDouble && !c.isCleared && isWall(c))) {
        warn(`The double flag on (${c.x}, ${c.y}) is ignored — only a box can hold another.`, c.x, c.y);
    }
    for (const c of cells.filter((c) => c.isJumpingJack)) {
        if (isWall(c)) {
            warn(`The jumping jack flag on (${c.x}, ${c.y}) is ignored — only a box can be a jumping jack.`, c.x, c.y);
        }
        else if (c.isSplit || c.isDouble) {
            warn(`The jumping jack flag on (${c.x}, ${c.y}) is ignored — a split or double box cannot be a jumping jack.`, c.x, c.y);
        }
        else if (c.isArrow || c.isGlide) {
            warn(`The arrow/glide flag on the jumping jack at (${c.x}, ${c.y}) is ignored — a jumping jack takes only the mystery flag.`, c.x, c.y);
        }
    }
    // A double box is a plain box holding another: the game builds it without any other box flag, and a seal
    // or a link on it is not handled by the game yet (mirrors TemplateValidator.cs).
    const allModifiers = (Array.isArray(t.cells?.modifiers) ? t.cells.modifiers : []).filter((m) => m);
    for (const c of cells.filter((c) => c.isDouble && !isWall(c))) {
        if (c.isMystery || c.isArrow || c.isGlide || c.isSplit) {
            warn(`The mystery/arrow/glide/split flag on the double box at (${c.x}, ${c.y}) is ignored — a double box is a plain box holding another.`, c.x, c.y);
        }
        if (allModifiers.some((m) => modifierCovers(m, c.x, c.y))) {
            warn(`The double box at (${c.x}, ${c.y}) carries a seal or a link — the game does not support those on a double box yet.`, c.x, c.y);
        }
    }
    // A double box holds a box of another colour, which needs a palette of two.
    const doubleBoxes = cells.filter((c) => c.isDouble && !isWall(c)).length;
    if (doubleBoxes > 0 && gen && paletteSize === 1) {
        warn(`The palette has 1 colour, so the level's ${doubleBoxes} double box(es) hold a box of their own colour.`);
    }
    const splitBoxes = cells.filter((c) => c.isSplit && !c.isDouble && !c.isCleared && !isWall(c)).length;
    if (splitBoxes > 0 && gen && paletteSize >= 1 && paletteSize < SPLIT_COLUMNS) {
        warn(`The palette has ${paletteSize} colour(s), so the level's ${splitBoxes} split box(es) cannot each show three different colours.`);
    }
    for (const c of cells.filter((c) => c.isArrow && !isWall(c))) {
        const s = step(c.arrowDirection);
        const ax = c.x + s.x;
        const ay = c.y + s.y;
        if (ax < 0 || ay < 0 || ax >= columns || ay >= rows) {
            warn(`Arrow at (${c.x}, ${c.y}) points off the board.`, c.x, c.y);
        }
    }
    for (const m of modifiers) {
        // `cells` arrives off Drive with no schema check, so the key can be missing or hold something that
        // is not an array — and this runs inside render() with no error boundary, so a throw here does not
        // report a bad file, it blanks the editor (the same reason mysterySliceChances is Array.isArray'd
        // above). The C# copy skips these too — `m?.Cells == null` covers a null modifier AND a null
        // `Cells`, which is why the list above is filtered before this loop as well as checked inside it.
        const mcells = Array.isArray(m.cells) ? m.cells : null;
        if (!mcells) {
            continue;
        }
        if (m.type === CellModifierType.FrozenBox || m.type === CellModifierType.SuperMysteryBox) {
            validateSingleCover(m, mcells, cellAt, warn);
            continue;
        }
        if (m.type === CellModifierType.SlidingDoor || m.type === CellModifierType.LaserGate || m.type === CellModifierType.Bolt) {
            validateRun(m, mcells, columns, rows, cellAt, err, warn);
            continue;
        }
        if (m.type === CellModifierType.MetalBox) {
            validateMetalBox(m, mcells, columns, rows, cellAt, err, warn);
            continue;
        }
        if (m.type === CellModifierType.Elevator) {
            validateElevator(mcells, columns, rows, cellAt, err, warn);
            continue;
        }
        if (m.type === CellModifierType.LockPanel) {
            // A lock panel names the CORNERS of an area, not two cells that must hold boxes — a 3×3 square
            // rarely has boxes in all four corners. It is judged on whether it covers anything at all.
            //
            // `cells` arrives unchecked off Drive (no schema checking), so it can be malformed: empty, a
            // single corner, or more than two. Both validator copies pin themselves to the documented
            // contract — cells[0] = start, cells[1] = end — so this copy and the C# copy agree cell-for-cell
            // on every shape, or the editor's live warning and the Publish gate would disagree.
            const start = mcells[0];
            if (!start) {
                continue; // nothing sensible to report a position for
            }
            const end = mcells[1] || start;
            const minx = Math.min(start.x, end.x);
            const miny = Math.min(start.y, end.y);
            const maxx = Math.max(start.x, end.x);
            const maxy = Math.max(start.y, end.y);
            const coversAny = cells.some((c) => c.x >= minx && c.x <= maxx && c.y >= miny && c.y <= maxy);
            if (!coversAny) {
                warn(`Lock panel at (${minx}, ${miny}) covers no cells.`, minx, miny);
            }
            continue;
        }
        // Word for word what TemplateValidator.cs writes, down to the leading type name — the two copies
        // have to agree exactly, and the C# one is authoritative (it gates Publish). `CellModifierType[…]`
        // gives the name for a known value; an unknown one falls back to the number, which is what C#
        // interpolation prints for an enum value with no name.
        // Only a NUMBER is looked up: on a numeric TS enum `CellModifierType["Connected"]` answers the
        // name→value direction too, so a hand-edited `"type": "Connected"` printed "0 modifier references…".
        // (The same trap `LevelDifficulty` is guarded against above.) A string type cannot bind on the
        // backend at all, so it 400s there — this copy only has to avoid inventing a name for it.
        const typeName = typeof m.type === "number" ? CellModifierType[m.type] ?? String(m.type) : String(m.type);
        for (const p of mcells) {
            if (!seen.has(`${p.x},${p.y}`)) {
                warn(`${typeName} modifier references empty cell (${p.x}, ${p.y}).`, p.x, p.y);
                // One word per endpoint. `cellAt` has no entry for an empty cell, so the rule below could
                // not fire here anyway — this says so out loud rather than leaving it to be re-derived, and
                // keeps the guarantee if that map ever starts holding more than it does today.
                continue;
            }
            // CellSetGenerator.IsConnectable: "Connectors only link two boxes, so tunnels and gate cells
            // (both walls, not boxes) are excluded." The editor offered Connect on any occupied cell until
            // now, so saved files hold links onto walls — and the ends of a link travel together when one is
            // dragged, which for a gate meant carrying one cell of it out of the wall.
            const at = cellAt.get(`${p.x},${p.y}`);
            if (at && isWall(at)) {
                warn(`${typeName} modifier links (${p.x}, ${p.y}), which is not a box — connectors only link boxes.`, p.x, p.y);
            }
        }
    }
    // Two seals on one box (mirrors the C# copy). Every seal the game has is a counter over the box's cell, so
    // stacking them does not add up to anything designed — the first to lift reveals nothing while the other
    // still holds, and the art draws one over the other.
    const sealedBy = new Map();
    const reported = new Set();
    for (const m of modifiers) {
        if (!Array.isArray(m.cells) || m.cells.length === 0 || m.type === CellModifierType.Connected) {
            continue;
        }
        for (const [x, y] of sealedCells(m, m.cells, columns, rows)) {
            const key = `${x},${y}`;
            const first = sealedBy.get(key);
            if (first === undefined) {
                sealedBy.set(key, sealName(m.type));
            }
            else if (!reported.has(key)) {
                reported.add(key);
                warn(`Cell (${x}, ${y}) is under both ${first} and ${sealName(m.type)} — one box takes one seal.`, x, y);
            }
        }
    }
    return issues;
}
// The words a message uses for each seal. Mirrors SealName in TemplateValidator.cs.
function sealName(type) {
    switch (type) {
        case CellModifierType.LockPanel:
            return "a lock panel";
        case CellModifierType.MetalBox:
            return "a metal box";
        case CellModifierType.Bolt:
            return "a bolt";
        case CellModifierType.FrozenBox:
            return "ice";
        case CellModifierType.LaserGate:
            return "a laser gate";
        case CellModifierType.SlidingDoor:
            return "a sliding door";
        case CellModifierType.SuperMysteryBox:
            return "a super mystery box";
        case CellModifierType.Elevator:
            return "an elevator";
        default:
            return String(type);
    }
}
// The cells a seal holds shut, for the overlap rule — clipped to the board (mirrors SealedCells in C#).
function sealedCells(m, mcells, columns, rows) {
    const a = mcells[0];
    let b = mcells[1] ?? a;
    if (m.type === CellModifierType.FrozenBox || m.type === CellModifierType.SuperMysteryBox) {
        b = a;
    }
    else if (m.type === CellModifierType.MetalBox && mcells.length === 1) {
        b = { x: a.x + 1, y: a.y + 1 }; // the game implies the 2x2 from its top-left cell
    }
    else if ((m.type === CellModifierType.SlidingDoor || m.type === CellModifierType.LaserGate || m.type === CellModifierType.Bolt) && (mcells.length < 2 || (a.x !== b.x && a.y !== b.y))) {
        return []; // reported on its own; a run with no second end, or a diagonal, names no cells
    }
    const out = [];
    for (let y = Math.max(0, Math.min(a.y, b.y)); y <= Math.min(rows - 1, Math.max(a.y, b.y)); y++) {
        for (let x = Math.max(0, Math.min(a.x, b.x)); x <= Math.min(columns - 1, Math.max(a.x, b.x)); x++) {
            out.push([x, y]);
        }
    }
    return out;
}
// Ice and the super mystery box: a cover over ONE box (mirrors ValidateSingleCover in C#).
function validateSingleCover(m, mcells, cellAt, warn) {
    const p = mcells[0];
    if (!p) {
        return; // nothing sensible to report a position for
    }
    const name = m.type === CellModifierType.FrozenBox ? "Ice" : "Super mystery box";
    const q = mcells[1];
    if (q && (q.x !== p.x || q.y !== p.y)) {
        warn(`${name} at (${p.x}, ${p.y}) also names (${q.x}, ${q.y}); it covers one box, so the game only uses the first.`, p.x, p.y);
    }
    const at = cellAt.get(`${p.x},${p.y}`);
    if (!at) {
        warn(`${name} at (${p.x}, ${p.y}) covers an empty cell.`, p.x, p.y);
    }
    else if (isWall(at)) {
        warn(`${name} at (${p.x}, ${p.y}) covers a wall (a gate, tunnel, tool box or super tunnel) — it can only cover a box.`, p.x, p.y);
    }
    const counter = Number.isFinite(m.counter) ? m.counter : 0; // the backend binds a missing counter as 0
    if (m.type === CellModifierType.FrozenBox && (counter < 1 || counter > FROZEN_BOX_HITS)) {
        warn(`Ice at (${p.x}, ${p.y}) takes ${counter} hits; it must be 1..${FROZEN_BOX_HITS} (the game reads it as ${FROZEN_BOX_HITS}).`, p.x, p.y);
    }
}
// The sliding door and the laser gate: a straight run between cells[0] and cells[1], plus the laser gate's
// KEY at cells[2] (mirrors ValidateRun in C#, message for message).
// The metal box (mirrors ValidateMetalBox in C#, message for message).
function validateMetalBox(m, mcells, columns, rows, cellAt, err, warn) {
    const a = mcells[0];
    if (!a) {
        return; // nothing sensible to report a position for
    }
    const b = mcells[1] ?? { x: a.x + 1, y: a.y + 1 };
    const minx = Math.min(a.x, b.x);
    const maxx = Math.max(a.x, b.x);
    const miny = Math.min(a.y, b.y);
    const maxy = Math.max(a.y, b.y);
    if (minx < 0 || miny < 0 || maxx >= columns || maxy >= rows) {
        err(`Metal box at (${minx}, ${miny}) runs off the board.`, minx, miny);
        return;
    }
    const w = maxx - minx + 1;
    const h = maxy - miny + 1;
    if (w !== 2 || h !== 2) {
        warn(`Metal box at (${minx}, ${miny}) covers ${w}×${h}; the game's art is made for 2×2 and will stretch.`, minx, miny);
    }
    const counter = Number.isFinite(m.counter) ? m.counter : 0; // the backend binds a missing counter as 0
    if (counter < 1 || counter > METAL_BOX_HITS) {
        const used = counter <= 0 ? METAL_BOX_HITS : Math.min(counter, METAL_BOX_HITS);
        warn(`Metal box at (${minx}, ${miny}) takes ${counter} hits; it must be 1..${METAL_BOX_HITS} (the game reads it as ${used}).`, minx, miny);
    }
    let coversAny = false;
    for (let y = miny; y <= maxy; y++) {
        for (let x = minx; x <= maxx; x++) {
            const at = cellAt.get(`${x},${y}`);
            if (!at) {
                continue;
            }
            coversAny = true;
            if (isWall(at)) {
                warn(`Metal box at (${minx}, ${miny}) covers (${x}, ${y}), which is not a box.`, x, y);
            }
        }
    }
    if (!coversAny) {
        warn(`Metal box at (${minx}, ${miny}) covers no cells.`, minx, miny);
    }
    let boxBeside = false;
    for (let y = miny - 1; y <= maxy + 1 && !boxBeside; y++) {
        for (let x = minx - 1; x <= maxx + 1 && !boxBeside; x++) {
            const inside = x >= minx && x <= maxx && y >= miny && y <= maxy;
            const corner = (x < minx || x > maxx) && (y < miny || y > maxy);
            const o = cellAt.get(`${x},${y}`);
            boxBeside = !inside && !corner && o !== undefined && !isWall(o);
        }
    }
    if (!boxBeside) {
        warn(`Metal box at (${minx}, ${miny}) has no box beside it — only a box dropped next to it can break it open.`, minx, miny);
    }
}
function validateElevator(mcells, columns, rows, cellAt, err, warn) {
    const a = mcells[0];
    if (!a) {
        return;
    }
    const b = mcells[1] ?? { x: a.x + ELEVATOR_SIZE - 1, y: a.y + ELEVATOR_SIZE - 1 };
    const minx = Math.min(a.x, b.x);
    const maxx = Math.max(a.x, b.x);
    const miny = Math.min(a.y, b.y);
    const maxy = Math.max(a.y, b.y);
    if (minx < 0 || miny < 0 || maxx >= columns || maxy >= rows) {
        err(`Elevator at (${minx}, ${miny}) runs off the board.`, minx, miny);
        return;
    }
    for (let y = miny; y <= maxy; y++) {
        for (let x = minx; x <= maxx; x++) {
            const at = cellAt.get(`${x},${y}`);
            // A propeller leaves its cell as it takes off, so it doesn't hold the lift shut (mirrors TemplateValidator.cs).
            if (at && isWall(at) && !(at.isPropeller && !at.isCleared)) {
                warn(`Elevator at (${minx}, ${miny}) stands under (${x}, ${y}), which is not a box.`, x, y);
            }
        }
    }
}
function validateRun(m, mcells, columns, rows, cellAt, err, warn) {
    const door = m.type === CellModifierType.SlidingDoor;
    const bolt = m.type === CellModifierType.Bolt;
    const name = door ? "Sliding door" : bolt ? "Bolt" : "Laser gate";
    if (mcells.length < 2) {
        const only = mcells[0] ?? { x: -1, y: -1 };
        err(door
            ? "Sliding door needs two cells — its anchor and its far end."
            : bolt
                ? "Bolt needs two cells — its head and its far end."
                : "Laser gate needs three cells — its two ends and its key box.", only.x, only.y);
        return;
    }
    const a = mcells[0];
    const b = mcells[1];
    const run = `from (${a.x}, ${a.y}) to (${b.x}, ${b.y})`;
    const onBoard = (p) => p.x >= 0 && p.y >= 0 && p.x < columns && p.y < rows;
    if (!onBoard(a) || !onBoard(b)) {
        err(`${name} ${run} runs off the board.`, a.x, a.y);
        return;
    }
    if (a.x !== b.x && a.y !== b.y) {
        err(`${name} ${run} is not a straight run — both ends must share a row or a column.`, a.x, a.y);
        return;
    }
    const length = Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) + 1;
    const min = door ? SLIDING_DOOR_MIN_LENGTH : bolt ? BOLT_MIN_LENGTH : LASER_GATE_MIN_LENGTH;
    if (length < min) {
        err(`${name} at (${a.x}, ${a.y}) is ${length} cell long; it needs at least ${min}.`, a.x, a.y);
        return;
    }
    // The backend binds a missing counter as 0, so read it that way here too.
    const counter = Number.isFinite(m.counter) ? m.counter : 0;
    if (door && (counter < min || counter > length)) {
        const used = counter <= 0 ? length : Math.min(counter, length);
        warn(`Sliding door ${run} starts ${counter} cells long; it must be ${min}..${length} (the game reads it as ${used}).`, a.x, a.y);
    }
    if (bolt && length !== BOLT_LENGTH) {
        warn(`Bolt ${run} runs through ${length} boxes; the game's bolt art is made for ${BOLT_LENGTH} and will not fit.`, a.x, a.y);
    }
    if (m.type === CellModifierType.LaserGate && a.x === b.x) {
        warn(`Laser gate ${run} runs vertically — the game's laser art is only tuned for horizontal runs, so its emitters will sit off the run.`, a.x, a.y);
    }
    const dx = Math.sign(b.x - a.x);
    const dy = Math.sign(b.y - a.y);
    for (let i = 0; i < length; i++) {
        const x = a.x + dx * i;
        const y = a.y + dy * i;
        const at = cellAt.get(`${x},${y}`);
        if (!at) {
            warn(`${name} ${run} runs over an empty cell (${x}, ${y}).`, x, y);
        }
        else if (isWall(at)) {
            warn(`${name} ${run} runs over (${x}, ${y}), which is not a box.`, x, y);
        }
    }
    if (door) {
        return;
    }
    if (bolt) {
        // Only the box just beyond the head, continuing the line, unlocks it — no other neighbour.
        const u = { x: a.x + Math.sign(a.x - b.x), y: a.y + Math.sign(a.y - b.y) };
        const ub = cellAt.get(`${u.x},${u.y}`);
        if (u.x < 0 || u.y < 0 || u.x >= columns || u.y >= rows) {
            err(`Bolt ${run} has its head against the edge — the box that unlocks it would stand at (${u.x}, ${u.y}), off the board, so nothing can ever unlock it.`, a.x, a.y);
        }
        else if (!ub) {
            warn(`Bolt ${run} is unlocked by the box at (${u.x}, ${u.y}), which is empty — unless a spawner fills it, nothing can ever unlock it.`, u.x, u.y);
        }
        else if (isWall(ub)) {
            err(`Bolt ${run} is unlocked by dropping (${u.x}, ${u.y}), which is not a box — nothing can ever unlock it.`, u.x, u.y);
        }
        return;
    }
    const k = mcells[2];
    if (!k) {
        err(`Laser gate ${run} has no key box — nothing can ever open it.`, a.x, a.y);
        return;
    }
    const inRun = dx === 0
        ? k.x === a.x && k.y >= Math.min(a.y, b.y) && k.y <= Math.max(a.y, b.y)
        : k.y === a.y && k.x >= Math.min(a.x, b.x) && k.x <= Math.max(a.x, b.x);
    const key = cellAt.get(`${k.x},${k.y}`);
    if (inRun) {
        err(`Laser gate ${run} has its key (${k.x}, ${k.y}) inside its own run — the key box is sealed by the gate it opens.`, k.x, k.y);
    }
    else if (!key) {
        err(`Laser gate ${run} has its key on an empty cell (${k.x}, ${k.y}) — nothing can ever open it.`, k.x, k.y);
    }
    else if (isWall(key)) {
        err(`Laser gate ${run} has its key on (${k.x}, ${k.y}), which is not a box — only a dropped box carries the key.`, k.x, k.y);
    }
}
